import type React from 'react'
import { useSyncExternalStore } from 'react'
import { BREAKPOINT_PX } from '../components/breakpoint'
import { useMetroMapStore } from '../state/useMetroMapStore'
import { LineCardOverlay } from './LineCardOverlay'
import { LineDetailsPanel } from './LineDetailsPanel'
import { useLineStatus } from './useLineStatus'
import { useLineView } from './useLineView'
import { useLineViewActions } from './useLineViewActions'

export interface LineViewHostProps {
  /** `panel` — inside the tablet/desktop side panel; `card` — the phone bottom card. */
  readonly surface: 'panel' | 'card'
}

/**
 * Mounts the line view's UI while it owns the map surface: a line is
 * selected, no station is open over it and no route lifecycle is running
 * (precedence route > station > line). The phone card also renders only
 * below the tablet breakpoint — the side panel hosts the desktop one.
 */
export function LineViewHost({ surface }: LineViewHostProps): React.ReactElement | null {
  const store = useMetroMapStore()
  const view = useLineView()
  const status = useLineStatus(view?.lineId ?? null)
  const actions = useLineViewActions(view, surface)
  const station = useSyncExternalStore(store.subscribe, () => store.getState().selectedStation)
  const routeStatus = useSyncExternalStore(store.subscribe, () => store.getState().routeStatus)
  const isExpanded = useSyncExternalStore(
    (callback) => {
      const mql = window.matchMedia(`(min-width: ${BREAKPOINT_PX}px)`)
      mql.addEventListener('change', callback)
      return () => mql.removeEventListener('change', callback)
    },
    () => window.matchMedia(`(min-width: ${BREAKPOINT_PX}px)`).matches,
  )
  if (view === null || station !== null || routeStatus.kind !== 'collapsed') return null
  if (surface === 'panel') return <LineDetailsPanel view={view} status={status} actions={actions} />
  if (isExpanded) return null
  return <LineCardOverlay view={view} status={status} actions={actions} />
}
